// Theme manager for applying different visual themes
export class ThemeManager {
  private static currentTheme: string = 'default';

  // CSS custom properties for different themes
  private static themes = {
    default: {
      '--pixel-bg-primary': '#2c3e50',
      '--pixel-bg-secondary': '#34495e',
      '--pixel-bg-light': '#ecf0f1',
      '--pixel-text-primary': '#ecf0f1',
      '--pixel-text-secondary': '#ffffff',
      '--pixel-text-dark': '#2c3e50',
      '--pixel-border': '#34495e',
      '--pixel-border-light': '#7f8c8d',
      '--pixel-primary': '#3498db',
      '--pixel-secondary': '#2ecc71',
      '--pixel-success': '#27ae60',
      '--pixel-danger': '#e74c3c',
      '--pixel-warning': '#f39c12',
      '--pixel-info': '#00bcd4',
      '--pixel-player': '#ff6b9d',
      '--pixel-player-shadow': '#c44569',
      '--pixel-glow': 'rgba(52, 152, 219, 0.6)',
      '--pixel-shadow': 'rgba(0, 0, 0, 0.3)',
    },
    dark: {
      '--pixel-bg-primary': '#1a1a1a',
      '--pixel-bg-secondary': '#2d2d2d',
      '--pixel-bg-light': '#3d3d3d',
      '--pixel-text-primary': '#ffffff',
      '--pixel-text-secondary': '#b0b0b0',
      '--pixel-text-dark': '#ffffff',
      '--pixel-border': '#4d4d4d',
      '--pixel-border-light': '#777777',
      '--pixel-primary': '#4ecdc4',
      '--pixel-secondary': '#45b7aa',
      '--pixel-success': '#52c41a',
      '--pixel-danger': '#ff4757',
      '--pixel-warning': '#ffa502',
      '--pixel-info': '#5f9fd8',
      '--pixel-player': '#ff6b9d',
      '--pixel-player-shadow': '#c44569',
      '--pixel-glow': 'rgba(78, 205, 196, 0.8)',
      '--pixel-shadow': 'rgba(0, 0, 0, 0.5)',
    },
    light: {
      '--pixel-bg-primary': '#ffffff',
      '--pixel-bg-secondary': '#f5f5f5',
      '--pixel-bg-light': '#fafafa',
      '--pixel-text-primary': '#333333',
      '--pixel-text-secondary': '#777777',
      '--pixel-text-dark': '#000000',
      '--pixel-border': '#e0e0e0',
      '--pixel-border-light': '#f0f0f0',
      '--pixel-primary': '#2196f3',
      '--pixel-secondary': '#4caf50',
      '--pixel-success': '#8bc34a',
      '--pixel-danger': '#f44336',
      '--pixel-warning': '#ff9800',
      '--pixel-info': '#03a9f4',
      '--pixel-player': '#e91e63',
      '--pixel-player-shadow': '#ad1457',
      '--pixel-glow': 'rgba(33, 150, 243, 0.4)',
      '--pixel-shadow': 'rgba(0, 0, 0, 0.2)',
    },
    retro: {
      '--pixel-bg-primary': '#0f380f',
      '--pixel-bg-secondary': '#306230',
      '--pixel-bg-light': '#8bac0f',
      '--pixel-text-primary': '#9bbc0f',
      '--pixel-text-secondary': '#8bac0f',
      '--pixel-text-dark': '#0f380f',
      '--pixel-border': '#306230',
      '--pixel-border-light': '#8bac0f',
      '--pixel-primary': '#9bbc0f',
      '--pixel-secondary': '#8bac0f',
      '--pixel-success': '#9bbc0f',
      '--pixel-danger': '#0f380f',
      '--pixel-warning': '#306230',
      '--pixel-info': '#8bac0f',
      '--pixel-player': '#9bbc0f',
      '--pixel-player-shadow': '#306230',
      '--pixel-glow': 'rgba(155, 188, 15, 0.6)',
      '--pixel-shadow': 'rgba(15, 56, 15, 0.5)',
    }
  };

  static initialize(): void {
    const savedTheme = localStorage.getItem('theme') || 'default';
    this.applyTheme(savedTheme);
  }

  static applyTheme(themeName: string): void {
    const theme = this.themes[themeName as keyof typeof this.themes] || this.themes.default;
    this.currentTheme = themeName;

    // Persist selection
    localStorage.setItem('theme', themeName);

    // Apply CSS custom properties to root
    const root = document.documentElement;
    Object.entries(theme).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });

    // Add theme class to body
    document.body.className = document.body.className.replace(/theme-\w+/, '');
    document.body.classList.add(`theme-${themeName}`);
  }

  static getCurrentTheme(): string {
    return this.currentTheme;
  }

  static getAvailableThemes(): string[] {
    return Object.keys(this.themes);
  }
}

export default ThemeManager;